/**
 * Session and inactivity timings, read from `public/config.json` at startup.
 *
 * These live here rather than as constants scattered through the code so an
 * operator can retune them per environment without a rebuild — config.json is
 * fetched at runtime, so one build serves every deployment.
 *
 * Why a module and not just React context: `activity.ts`, `token.ts` and the
 * Axios interceptor all need these values and none of them are components.
 * ConfigProvider calls `setTimings()` once, before any child renders, and
 * everything else reads `timings()`.
 *
 * The values below are a last-resort safety net, NOT the intended source of
 * truth — that is config.json. They exist only so that a config file missing a
 * key degrades to something sensible instead of filling the timers with NaN.
 */

export interface Timings {
  /**
   * How long a login lasts before the auth server refuses to refresh it,
   * in minutes.
   *
   * This is the *server-side* backstop for the inactivity timeout. If someone
   * just closes the tab, no client code runs to sign them out — the session
   * simply lapses this long after its last refresh. Keep it a little larger
   * than `idleTimeoutMinutes`.
   *
   * The auth server caps this with its own `maxTokenLifeTime`, so asking for
   * more than the auth server allows silently gets you the auth server's
   * number.
   */
  tokenLifeTimeMinutes: number;

  /**
   * How long a user may sit doing nothing before being signed out, in minutes.
   * The countdown warning appears inside this window, not after it.
   */
  idleTimeoutMinutes: number;

  /**
   * How long the "Still there?" warning counts down for, in minutes.
   *
   * Taken from the END of the idle window: with a 30-minute timeout and a
   * 2-minute warning, the dialog appears at 28 minutes and sign-out happens at
   * 30. Must be smaller than `idleTimeoutMinutes`.
   */
  idleWarningMinutes: number;

  /**
   * How long the "session ended" dialog waits before returning the user to the
   * login page on its own, in seconds.
   *
   * The dialog is terminal — there is nothing to do but leave — so its button
   * only skips the wait. Long enough to read the reason, short enough that an
   * unattended screen goes back to the login page by itself.
   *
   * Applies to the inactivity and expiry endings only. The "signed in on
   * another device" one waits for the button, because it carries a support
   * phone number and a dialog that erases a phone number while somebody is
   * dialling it is worse than one that waits.
   *
   * There is no way to switch the redirect off from here: `0` fails the
   * positive-number check below and falls back to the default, same as any
   * other typo. If a deployment ever needs "wait for the button" everywhere,
   * add a separate boolean rather than overloading 0.
   */
  sessionEndedRedirectSeconds: number;

  /**
   * How often the portal asks its own server "is my session still alive?", in
   * seconds. This is what lets an idle tab discover it was signed out
   * somewhere else, instead of waiting for the user's next click.
   *
   * Lower = the user finds out sooner, at the cost of more requests.
   */
  sessionPollSeconds: number;

  /**
   * How often the single background timer runs, in seconds. Everything else —
   * idle maths, expiry checks, the poll, the token refresh — is scheduled off
   * this one tick, so nothing can fire out of order.
   *
   * Rarely worth changing. It bounds how late a sign-out can be.
   */
  heartbeatSeconds: number;

  /**
   * How often "the user is active" is written to browser storage, in seconds.
   *
   * Activity is tracked in memory on every event; this only throttles the
   * shared write that other tabs read. Rarely worth changing.
   */
  activityPersistSeconds: number;

  /**
   * How much the user's clock is allowed to disagree with the server's before
   * we treat a token as expired, in seconds.
   *
   * Guards against a PC whose clock runs fast signing itself out of a session
   * the server is still perfectly happy with. Rarely worth changing.
   */
  clockSkewSeconds: number;
}

export const DEFAULT_TIMINGS: Timings = {
  tokenLifeTimeMinutes: 35,
  idleTimeoutMinutes: 30,
  idleWarningMinutes: 2,
  sessionEndedRedirectSeconds: 10,
  sessionPollSeconds: 20,
  heartbeatSeconds: 5,
  activityPersistSeconds: 5,
  clockSkewSeconds: 30,
};

let current: Timings = { ...DEFAULT_TIMINGS };

/** Positive finite numbers only — a typo must not disable a timer. */
const positive = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : fallback;

export const setTimings = (raw: Partial<Record<keyof Timings, unknown>> = {}) => {
  const next: Timings = {
    tokenLifeTimeMinutes: positive(
      raw.tokenLifeTimeMinutes,
      DEFAULT_TIMINGS.tokenLifeTimeMinutes,
    ),
    idleTimeoutMinutes: positive(
      raw.idleTimeoutMinutes,
      DEFAULT_TIMINGS.idleTimeoutMinutes,
    ),
    idleWarningMinutes: positive(
      raw.idleWarningMinutes,
      DEFAULT_TIMINGS.idleWarningMinutes,
    ),
    sessionEndedRedirectSeconds: positive(
      raw.sessionEndedRedirectSeconds,
      DEFAULT_TIMINGS.sessionEndedRedirectSeconds,
    ),
    sessionPollSeconds: positive(
      raw.sessionPollSeconds,
      DEFAULT_TIMINGS.sessionPollSeconds,
    ),
    heartbeatSeconds: positive(
      raw.heartbeatSeconds,
      DEFAULT_TIMINGS.heartbeatSeconds,
    ),
    activityPersistSeconds: positive(
      raw.activityPersistSeconds,
      DEFAULT_TIMINGS.activityPersistSeconds,
    ),
    clockSkewSeconds: positive(
      raw.clockSkewSeconds,
      DEFAULT_TIMINGS.clockSkewSeconds,
    ),
  };

  // A warning longer than the whole idle window would latch the moment the
  // user stops typing and never count down properly.
  if (next.idleWarningMinutes >= next.idleTimeoutMinutes) {
    // eslint-disable-next-line no-console
    console.warn(
      `[config] idleWarningMinutes (${next.idleWarningMinutes}) must be less than ` +
        `idleTimeoutMinutes (${next.idleTimeoutMinutes}); falling back to ` +
        `${DEFAULT_TIMINGS.idleWarningMinutes}`,
    );
    next.idleWarningMinutes = Math.min(
      DEFAULT_TIMINGS.idleWarningMinutes,
      next.idleTimeoutMinutes / 2,
    );
  }

  // If the token dies before the idle timeout fires, users get "session
  // expired" where "you were inactive" was intended. Not fatal, so warn
  // rather than override an explicit choice.
  if (next.tokenLifeTimeMinutes <= next.idleTimeoutMinutes) {
    // eslint-disable-next-line no-console
    console.warn(
      `[config] tokenLifeTimeMinutes (${next.tokenLifeTimeMinutes}) should exceed ` +
        `idleTimeoutMinutes (${next.idleTimeoutMinutes}), or sessions expire ` +
        `before the inactivity warning can run`,
    );
  }

  current = next;
};

export const timings = (): Timings => current;

// ── derived helpers, so callers are not doing arithmetic everywhere ─────────

export const tokenLifeTimeSeconds = () => timings().tokenLifeTimeMinutes * 60;
export const idleTimeoutMs = () => timings().idleTimeoutMinutes * 60 * 1000;
export const idleWarningMs = () => timings().idleWarningMinutes * 60 * 1000;
export const heartbeatMs = () => timings().heartbeatSeconds * 1000;
export const activityPersistMs = () => timings().activityPersistSeconds * 1000;
export const clockSkewMs = () => timings().clockSkewSeconds * 1000;

/**
 * Floored at three seconds. `positive()` rejects NaN and negatives but happily
 * accepts 0.5, and half a second would flash the reason past the user — the
 * exact failure the dialog exists to prevent. Clamping in the helper rather
 * than in setTimings follows pollEveryTicks() below.
 */
export const sessionEndedRedirectMs = () =>
  Math.max(3000, timings().sessionEndedRedirectSeconds * 1000);

/**
 * The poll runs off the heartbeat, so express it as "every Nth tick". At least
 * 1, so a poll interval shorter than the heartbeat simply polls every tick.
 */
export const pollEveryTicks = () =>
  Math.max(1, Math.round(timings().sessionPollSeconds / timings().heartbeatSeconds));
